import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  AdminPage,
  AdminPanel,
} from "@/components/admin/AdminPrimitives";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type Status = "queued" | "scraping" | "classifying" | "completed" | "failed";

type AuditRow = {
  id: string;
  email: string;
  status: Status;
  inputAsins: string[];
  scrapedReviewCount: number;
  flaggedCount: number;
  createdAt: string;
};

type AuditsResponse = {
  audits: AuditRow[];
  page: number;
  pageSize: number;
  total: number;
};

const PAGE_SIZE = 50;

const statusVariant: Record<
  Status,
  "default" | "secondary" | "destructive" | "outline"
> = {
  queued: "secondary",
  scraping: "default",
  classifying: "default",
  completed: "outline",
  failed: "destructive",
};

function AdminListInner() {
  const navigate = useNavigate();
  const [audits, setAudits] = useState<AuditRow[] | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAudits(null);
    api<AuditsResponse>(
      `/admin/audits?page=${page}&pageSize=${PAGE_SIZE}`
    ).then((res) => {
      if (cancelled) return;
      if (!res.ok || !res.data) {
        setError(res.error ?? "Failed to load audits.");
        return;
      }
      setAudits(res.data.audits);
      setTotal(res.data.total);
    });
    return () => {
      cancelled = true;
    };
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(total, page * PAGE_SIZE);

  return (
    <AdminPage
      eyebrow="Records"
      title="Audits"
      description={
        total > 0
          ? `Showing ${start}-${end} of ${total} audit requests, newest first.`
          : "Audit requests, newest first."
      }
    >
      <AdminPanel>
        {error && <p className="px-4 py-3 text-sm text-destructive">{error}</p>}
        {!audits && !error && (
          <div className="flex items-center gap-2 px-4 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {audits && audits.length === 0 && (
          <p className="px-4 py-8 text-sm text-muted-foreground">No audits yet.</p>
        )}
        {audits && audits.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Lead</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">ASINs</TableHead>
                <TableHead className="text-right">Reviews</TableHead>
                <TableHead className="text-right">Flagged</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {audits.map((a) => {
                const href = `/admin/audits/${a.id}`;
                return (
                <TableRow
                  key={a.id}
                  onClick={() => navigate(href)}
                  onAuxClick={(e) => {
                    if (e.button === 1) window.open(href, "_blank");
                  }}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  <TableCell className="text-muted-foreground">
                    {a.createdAt.slice(0, 10)}
                  </TableCell>
                  <TableCell className="font-medium">{a.email}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant[a.status]}>{a.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {a.inputAsins.length}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {a.scrapedReviewCount}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {a.flaggedCount}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {audits && total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3 text-sm text-muted-foreground">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((n) => Math.max(1, n - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((n) => Math.min(totalPages, n + 1))}
                disabled={page >= totalPages}
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </AdminPanel>
    </AdminPage>
  );
}

export default function AdminList() {
  return (
    <AdminGate>
      <AdminLayout>
        <AdminListInner />
      </AdminLayout>
    </AdminGate>
  );
}
